import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, output, inject, input } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { noWhitespaceValidator } from '@core/common-legacy';
import { injectAuthTranslations, TwoFaService } from '@core/components-authorization-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { SecurityAnalytics } from '@core/security-logic';
import {
  NotificationsController,
  ButtonLoadingDirective,
  IconComponent,
  ButtonComponent,
  DialogFooterComponent,
  CodeInputComponent,
  LabelComponent,
  DialogBodyComponent,
  HeadingComponent,
  DialogHeaderComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-sms-activation-step-2',
  templateUrl: './sms-activation-step-2.component.html',
  styleUrls: ['./sms-activation-step-2.component.less'],
  imports: [
    DialogHeaderComponent,
    HeadingComponent,
    DialogBodyComponent,
    InfoboxComponent,
    LabelComponent,
    CodeInputComponent,
    ReactiveFormsModule,
    DialogFooterComponent,
    ButtonComponent,
    IconComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class SmsActivationStep2Component implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly notificationCtrl = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly analytics = inject(SecurityAnalytics);
  private readonly twoFaSmsService = inject<
    TwoFaService<{
      error?: string[];
      errors?: string[];
    }>
  >(TwoFaService);

  public readonly phoneNumber = input<string>(undefined!);
  public translationKey = injectAuthTranslations();

  public codeControl: FormControl<string>;
  public codeError: { error: string[] } | null;
  public isLoading = false;
  public isWarningVisible = false;

  public goBack = output<string | void>();
  public activated = output<void>();
  public cancel = output<void>();

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    this.codeControl = <FormControl<string>>(
      this.formBuilder.control('', [Validators.required, noWhitespaceValidator])
    );
  }

  public ngOnInit(): void {
    this.codeControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.cdRef.detectChanges());

    this.twoFaSmsService
      .getEnableSmsAuthSuccessAction()
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.notificationCtrl.success({
          title: this.i18n.translate(this.translationKey.dialog_2fa_sms.success_toast.title),
        });
        this.activated.emit();
        this.isLoading = false;
        this.cdRef.detectChanges();
      });

    this.twoFaSmsService
      .getEnableSmsAuthFailAction()
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(data => {
        const error = data.payload.error;
        this.codeError = { error: error.error ? error.error : error.errors! };
        this.isLoading = false;
        this.cdRef.detectChanges();
      });
  }

  public resendCode(): void {
    this.twoFaSmsService.sendSmsCode(this.phoneNumber());
    this.isWarningVisible = true;
    this.cdRef.detectChanges();
  }

  public enable(): void {
    this.analytics.trackActivateTwoFactorAuth('sms');
    this.isLoading = true;
    this.cdRef.detectChanges();
    this.twoFaSmsService.enableSmsAuth(this.phoneNumber(), this.codeControl.value);
  }

  public onGoBackClick(): void {
    this.goBack.emit();
  }

  public onCancelClick(): void {
    this.cancel.emit();
  }

  public onClearError(): void {
    this.codeError = null;
    this.cdRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
